import { storageKey, writeLocalJson, removeLocalKey, readLocalJsonOptional } from '@authe/fe-commons/storage';
import { defineChain, recoverMessageAddress, verifyMessage, createPublicClient, http as http$1 } from 'viem';
export { getAddress, isAddress } from 'viem';
import { ChainFamily, registry, getChainMetadata, toHexChainId, normalizeAddress } from '@authe/fe-commons/chains';
import { http, createConfig, injected, usePublicClient, useWalletClient, useSignMessage, WagmiProvider, useChainId, useSwitchChain, useAccount, useConnect, useDisconnect } from 'wagmi';
import { createLogger } from '@authe/fe-commons/logging';
import { blockArg } from '@authe/fe-commons/contracts';
import * as React11 from 'react';
import { TwoKeySigner } from '@authe/fe-commons/signing';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { jsx, jsxs } from 'react/jsx-runtime';
import { Select, SelectTrigger, SelectValue, cn, SelectContent, SelectItem, ChainAvatar, Badge, Button } from '@authe/ui-core';
import { truncateAddress } from '@authe/fe-commons/wallet';

// src/config.ts
var WEB3_PACKAGE = "web3";
var WEB3_SELECTED_CHAIN_KEY = storageKey(WEB3_PACKAGE, "chain");
var WEB3_EVM_CONNECTION_ID = "evm:wagmi";
var WEB3_DEFAULT_CHAIN_SLUG = "eth";
var WEB3_SIGNER_TTL_MS = 15 * 6e4;
var WEB3_SIGNER_ROOT_ID = "authe-web3";
var WEB3_DEFAULT_RPC_BASE = "https://ethereum-rpc.publicnode.com";
var FLAGS_KEY = storageKey(WEB3_PACKAGE, "disconnected");
function readFlags() {
  return readLocalJsonOptional(FLAGS_KEY) ?? {};
}
function markDisconnected(id) {
  const flags = readFlags();
  if (flags[id]) return;
  flags[id] = true;
  writeLocalJson(FLAGS_KEY, flags);
}
function clearDisconnected(id) {
  const flags = readFlags();
  if (!flags[id]) return;
  delete flags[id];
  if (Object.keys(flags).length === 0) removeLocalKey(FLAGS_KEY);
  else writeLocalJson(FLAGS_KEY, flags);
}
function isDisconnected(id) {
  return readFlags()[id] === true;
}

// src/chains/rpc.ts
var DEFAULT_RPC_BY_SLUG = {
  eth: [WEB3_DEFAULT_RPC_BASE, "https://cloudflare-eth.com"],
  polygon: ["https://polygon-bor-rpc.publicnode.com"],
  matic: ["https://polygon-bor-rpc.publicnode.com"],
  bsc: ["https://bsc-rpc.publicnode.com"],
  arbitrum: ["https://arbitrum-one-rpc.publicnode.com"],
  optimism: ["https://optimism-rpc.publicnode.com"],
  base: ["https://base-rpc.publicnode.com"],
  avalanche: ["https://avalanche-c-chain-rpc.publicnode.com"],
  fantom: ["https://rpcapi.fantom.network", "https://fantom.drpc.org"],
  gnosis: ["https://gnosis-rpc.publicnode.com"],
  celo: ["https://celo-rpc.publicnode.com"],
  linea: ["https://linea-rpc.publicnode.com"],
  scroll: ["https://scroll-rpc.publicnode.com"],
  zksync: ["https://mainnet.era.zksync.io"],
  zksync_era: ["https://mainnet.era.zksync.io"],
  mantle: ["https://mantle-rpc.publicnode.com"],
  blast: ["https://blast-rpc.publicnode.com"]
};
function resolveRpcUrls(chain, overrides) {
  if (overrides) {
    if (chain.chainIdHex && overrides[chain.chainIdHex]?.length) return overrides[chain.chainIdHex];
    if (overrides[chain.slug]?.length) return overrides[chain.slug];
    if (chain.mainnetSlug && overrides[chain.mainnetSlug]?.length) return overrides[chain.mainnetSlug];
  }
  return DEFAULT_RPC_BY_SLUG[chain.slug] ?? (chain.mainnetSlug ? DEFAULT_RPC_BY_SLUG[chain.mainnetSlug] ?? [] : []);
}
function toViemChain(chain, options = {}) {
  if (chain.family !== ChainFamily.EVM || !chain.chainIdHex) return null;
  const id = Number(BigInt(chain.chainIdHex));
  if (!Number.isSafeInteger(id) || id <= 0) return null;
  const urls = options.rpcUrls?.length ? options.rpcUrls : resolveRpcUrls(chain, options.overrides);
  if (!urls.length) return null;
  return defineChain({
    id,
    name: chain.name,
    nativeCurrency: { name: chain.nativeToken, symbol: chain.nativeToken, decimals: chain.decimals },
    rpcUrls: { default: { http: urls } },
    ...chain.explorerUrl ? { blockExplorers: { default: { name: `${chain.name} Explorer`, url: chain.explorerUrl } } } : {},
    testnet: chain.isTestnet
  });
}
function autheEvmViemChains(options = {}) {
  const wanted = options.slugs ? new Set(options.slugs) : null;
  const out = [];
  for (const chain of registry.all()) {
    if (chain.family !== ChainFamily.EVM) continue;
    if (!options.includeTestnets && chain.isTestnet) continue;
    if (wanted && !wanted.has(chain.slug)) continue;
    const viem = toViemChain(chain, { overrides: options.overrides });
    if (viem) out.push(viem);
  }
  return out;
}
var log = createLogger(`${WEB3_PACKAGE}:wagmi`);
function createAutheWagmiConfig(options = {}) {
  const resolved = options.chains?.length ? options.chains : autheEvmViemChains({
    includeTestnets: options.includeTestnets,
    overrides: options.rpc,
    slugs: options.slugs
  });
  if (resolved.length === 0) {
    throw new Error(
      "createAutheWagmiConfig: no EVM chains resolved from the registry \u2014 supply `rpc` overrides or explicit `chains`."
    );
  }
  const chains = resolved;
  const transports = Object.fromEntries(
    chains.map((c) => [c.id, options.transports?.[c.id] ?? http()])
  );
  log.debug("creating wagmi config", { chains: chains.map((c) => c.id) });
  return createConfig({
    chains,
    connectors: options.connectors ?? [injected()],
    transports,
    ssr: options.ssr
  });
}

// src/contract/keys.ts
function stableArgsKey(args) {
  if (!args || args.length === 0) return "";
  return args.map((a) => {
    if (typeof a === "bigint") return `${a}n`;
    try {
      return JSON.stringify(a);
    } catch {
      return String(a);
    }
  }).join("|");
}
var DEFAULT_LOG_WINDOW = 5000n;
function toError(reason) {
  return reason instanceof Error ? reason : new Error(String(reason));
}
function createRpcContractReader(options = {}) {
  const clients = /* @__PURE__ */ new Map();
  function clientFor(chainId) {
    const key = chainId.toLowerCase();
    const cached = clients.get(key);
    if (cached) return cached;
    const meta = getChainMetadata(chainId);
    if (!meta) throw new Error(`Unknown chain: ${chainId}`);
    const viemChain = toViemChain(meta, { overrides: options.rpcOverrides });
    if (!viemChain) throw new Error(`No RPC endpoint for chain ${meta.slug} (${chainId})`);
    const client = createPublicClient({ chain: viemChain, transport: http$1() });
    clients.set(key, client);
    return client;
  }
  return {
    id: "rpc",
    async read(ref, req) {
      const client = clientFor(ref.chainId);
      return client.readContract({
        address: ref.address,
        abi: req.abi,
        functionName: req.functionName,
        args: req.args,
        ...blockArg(ref.block)
      });
    },
    async readMany(ref, reqs) {
      if (reqs.length === 0) return [];
      const client = clientFor(ref.chainId);
      const settled = await Promise.allSettled(
        reqs.map(
          (r) => client.readContract({
            address: ref.address,
            abi: r.abi,
            functionName: r.functionName,
            args: r.args,
            ...blockArg(ref.block)
          })
        )
      );
      return settled.map(
        (s) => s.status === "fulfilled" ? { status: "success", data: s.value } : { status: "failure", error: toError(s.reason) }
      );
    },
    async getStorageAt(ref, slot) {
      const client = clientFor(ref.chainId);
      return client.getStorageAt({ address: ref.address, slot, ...blockArg(ref.block) });
    },
    async getLogs(ref, query = {}) {
      const client = clientFor(ref.chainId);
      const window2 = options.logWindow ?? DEFAULT_LOG_WINDOW;
      const toBlock = query.toBlock ?? (ref.block?.blockNumber != null ? ref.block.blockNumber : "latest");
      let fromBlock = query.fromBlock;
      if (fromBlock === void 0) {
        const head = typeof toBlock === "bigint" ? toBlock : await client.getBlockNumber();
        fromBlock = head > window2 ? head - window2 : 0n;
      }
      const logs = await client.getLogs({ address: ref.address, fromBlock, toBlock });
      return logs.map((log6) => ({
        address: log6.address,
        topics: log6.topics,
        data: log6.data,
        blockNumber: log6.blockNumber,
        logIndex: log6.logIndex,
        transactionHash: log6.transactionHash
      }));
    }
  };
}
var log2 = createLogger(`${WEB3_PACKAGE}:contract-read`);
function useContractRead(params) {
  const { address, abi, functionName, args, chainId, blockNumber, blockTag, enabled = true } = params;
  const client = usePublicClient(chainId != null ? { chainId } : void 0);
  const [data, setData] = React11.useState(void 0);
  const [error, setError] = React11.useState(null);
  const [isPending, setIsPending] = React11.useState(false);
  const [nonce, setNonce] = React11.useState(0);
  const ready = enabled && Boolean(client && address && abi && functionName);
  const argsKey = stableArgsKey(args);
  React11.useEffect(() => {
    if (!ready || !client || !address || !abi || !functionName) {
      setData(void 0);
      setError(null);
      setIsPending(false);
      return void 0;
    }
    let cancelled = false;
    setIsPending(true);
    setError(null);
    const block = blockNumber != null ? { blockNumber } : blockTag ? { blockTag } : {};
    client.readContract({ address, abi, functionName, args, ...block }).then((result) => {
      if (cancelled) return;
      setData(result);
      setIsPending(false);
    }).catch((err) => {
      if (cancelled) return;
      log2.warn("contract read failed", { address, functionName, error: String(err) });
      setData(void 0);
      setError(err instanceof Error ? err : new Error(String(err)));
      setIsPending(false);
    });
    return () => {
      cancelled = true;
    };
  }, [ready, client, address, functionName, argsKey, blockNumber, blockTag, nonce]);
  const refetch = React11.useCallback(() => setNonce((n) => n + 1), []);
  return { data, error, isPending, refetch };
}
var log3 = createLogger(`${WEB3_PACKAGE}:contract-reads`);
function useContractReads(params) {
  const { calls, chainId, blockNumber, blockTag, enabled = true } = params;
  const client = usePublicClient(chainId != null ? { chainId } : void 0);
  const [results, setResults] = React11.useState([]);
  const [isPending, setIsPending] = React11.useState(false);
  const [nonce, setNonce] = React11.useState(0);
  const ready = enabled && Boolean(client) && calls.length > 0;
  const callsKey = React11.useMemo(
    () => calls.map((c) => `${c.address}:${c.functionName}:${stableArgsKey(c.args)}`).join(";"),
    [calls]
  );
  React11.useEffect(() => {
    if (!ready || !client) {
      setResults([]);
      setIsPending(false);
      return void 0;
    }
    let cancelled = false;
    setIsPending(true);
    const block = blockNumber != null ? { blockNumber } : blockTag ? { blockTag } : {};
    Promise.allSettled(
      calls.map(
        (c) => client.readContract({ address: c.address, abi: c.abi, functionName: c.functionName, args: c.args, ...block })
      )
    ).then((settled) => {
      if (cancelled) return;
      setResults(
        settled.map(
          (s) => s.status === "fulfilled" ? { status: "success", data: s.value } : { status: "failure", error: s.reason instanceof Error ? s.reason : new Error(String(s.reason)) }
        )
      );
      setIsPending(false);
    }).catch((err) => {
      if (cancelled) return;
      log3.warn("batch read failed", { error: String(err) });
      setIsPending(false);
    });
    return () => {
      cancelled = true;
    };
  }, [ready, client, callsKey, blockNumber, blockTag, nonce]);
  const refetch = React11.useCallback(() => setNonce((n) => n + 1), []);
  return { results, isPending, refetch };
}
var log4 = createLogger(`${WEB3_PACKAGE}:contract-write`);
function useContractWrite(chainId) {
  const { data: walletClient } = useWalletClient(chainId != null ? { chainId } : void 0);
  const publicClient = usePublicClient(chainId != null ? { chainId } : void 0);
  const [hash, setHash] = React11.useState(null);
  const [receipt, setReceipt] = React11.useState(null);
  const [status, setStatus] = React11.useState("idle");
  const [error, setError] = React11.useState(null);
  const reset = React11.useCallback(() => {
    setHash(null);
    setReceipt(null);
    setStatus("idle");
    setError(null);
  }, []);
  const write = React11.useCallback(
    async (call, value) => {
      if (!walletClient) {
        const err = new Error("No connected wallet to sign the transaction.");
        setError(err);
        setStatus("error");
        return null;
      }
      setStatus("signing");
      setError(null);
      setReceipt(null);
      setHash(null);
      try {
        const txHash = await walletClient.writeContract({
          address: call.address,
          abi: call.abi,
          functionName: call.functionName,
          args: call.args,
          account: walletClient.account,
          chain: walletClient.chain,
          ...value != null ? { value } : {}
        });
        setHash(txHash);
        setStatus("pending");
        if (publicClient) {
          const r = await publicClient.waitForTransactionReceipt({ hash: txHash });
          setReceipt(r);
        }
        setStatus("success");
        return txHash;
      } catch (err) {
        log4.warn("contract write failed", { address: call.address, functionName: call.functionName, error: String(err) });
        setError(err instanceof Error ? err : new Error(String(err)));
        setStatus("error");
        return null;
      }
    },
    [walletClient, publicClient]
  );
  return { write, hash, receipt, status, error, reset };
}
var log5 = createLogger(`${WEB3_PACKAGE}:storage`);
function useEvmStorageAt(params) {
  const { address, slot, chainId, blockNumber, blockTag, enabled = true } = params;
  const client = usePublicClient(chainId != null ? { chainId } : void 0);
  const [data, setData] = React11.useState(void 0);
  const [error, setError] = React11.useState(null);
  const [isPending, setIsPending] = React11.useState(false);
  const [nonce, setNonce] = React11.useState(0);
  const ready = enabled && Boolean(client && address && slot);
  React11.useEffect(() => {
    if (!ready || !client || !address || !slot) {
      setData(void 0);
      setError(null);
      setIsPending(false);
      return void 0;
    }
    let cancelled = false;
    setIsPending(true);
    setError(null);
    const block = blockNumber != null ? { blockNumber } : blockTag ? { blockTag } : {};
    client.getStorageAt({ address, slot, ...block }).then((result) => {
      if (cancelled) return;
      setData(result);
      setIsPending(false);
    }).catch((err) => {
      if (cancelled) return;
      log5.warn("storage read failed", { address, slot, error: String(err) });
      setError(err instanceof Error ? err : new Error(String(err)));
      setIsPending(false);
    });
    return () => {
      cancelled = true;
    };
  }, [ready, client, address, slot, blockNumber, blockTag, nonce]);
  const refetch = React11.useCallback(() => setNonce((n) => n + 1), []);
  return { data, error, isPending, refetch };
}
function useAutheSigner(options = {}) {
  const { rootKeyId = WEB3_SIGNER_ROOT_ID, ttlMs = WEB3_SIGNER_TTL_MS, enabled = true } = options;
  const [signer, setSigner] = React11.useState(null);
  React11.useEffect(() => {
    if (!enabled) {
      setSigner(null);
      return;
    }
    let active = true;
    void TwoKeySigner.create({ rootKeyId, intermediateTtlMs: ttlMs }).then((created) => {
      if (active) setSigner(created);
    });
    return () => {
      active = false;
    };
  }, [enabled, rootKeyId, ttlMs]);
  const signRequest = React11.useCallback(
    (payload) => signer ? signer.signRequest(payload) : Promise.reject(new Error("Authe signer is not ready yet.")),
    [signer]
  );
  const exportRootPublicJwk = React11.useCallback(
    () => signer ? signer.exportRootPublicJwk() : Promise.reject(new Error("Authe signer is not ready yet.")),
    [signer]
  );
  return { signer, ready: signer !== null, signRequest, exportRootPublicJwk };
}
function useEvmSignMessage() {
  const { signMessageAsync, isPending, error } = useSignMessage();
  const signMessage = React11.useCallback(
    (message) => signMessageAsync({ message }),
    [signMessageAsync]
  );
  const signAndRecover = React11.useCallback(
    async (message) => {
      const signature = await signMessageAsync({ message });
      const signer = await recoverMessageAddress({ message, signature });
      return { signature, signer };
    },
    [signMessageAsync]
  );
  return { signMessage, signAndRecover, isPending, error: error ?? null };
}
function recoverSigner(message, signature) {
  return recoverMessageAddress({ message, signature });
}
function verifyEvmSignature(address, message, signature) {
  return verifyMessage({ address, message, signature });
}
function Web3Provider({
  config,
  queryClient,
  reconnectOnMount,
  children
}) {
  const client = React11.useMemo(() => queryClient ?? new QueryClient(), [queryClient]);
  const [resolvedReconnect] = React11.useState(
    () => reconnectOnMount ?? !isDisconnected(WEB3_EVM_CONNECTION_ID)
  );
  return /* @__PURE__ */ jsx(WagmiProvider, { config, reconnectOnMount: resolvedReconnect, children: /* @__PURE__ */ jsx(QueryClientProvider, { client, children }) });
}
function persistSlug(slug) {
  if (slug) writeLocalJson(WEB3_SELECTED_CHAIN_KEY, slug);
}
function useSelectedChain() {
  const chainId = useChainId();
  const { switchChain, isPending } = useSwitchChain();
  const chain = getChainMetadata(String(chainId));
  const switchToChainId = React11.useCallback(
    (id) => {
      switchChain({ chainId: id });
      persistSlug(getChainMetadata(String(id))?.slug);
    },
    [switchChain]
  );
  const switchToSlug = React11.useCallback(
    (slug) => {
      const hex = toHexChainId(slug);
      if (!hex) return false;
      const id = Number(BigInt(hex));
      if (!Number.isSafeInteger(id)) return false;
      switchChain({ chainId: id });
      persistSlug(slug);
      return true;
    },
    [switchChain]
  );
  return {
    chainId,
    chain,
    isSupported: chain !== null,
    isSwitching: isPending,
    switchToChainId,
    switchToSlug
  };
}
function useAddressInventory() {
  const { address, addresses, chainId, isConnected, status, connector } = useAccount();
  const entries = React11.useMemo(() => {
    if (!isConnected) return [];
    const list = addresses ?? (address ? [address] : []);
    const activeLower = address?.toLowerCase();
    return list.map((a) => ({
      address: a,
      normalized: normalizeAddress(a),
      isActive: a.toLowerCase() === activeLower
    }));
  }, [addresses, address, isConnected]);
  const active = React11.useMemo(() => entries.find((e) => e.isActive) ?? null, [entries]);
  const chain = chainId != null ? getChainMetadata(String(chainId)) : null;
  return { entries, active, chainId, chain, isConnected, status, connector: connector?.name };
}
function useWalletConnection() {
  const { address, isConnected } = useAccount();
  const { connect, connectors, isPending, error } = useConnect();
  const { disconnect } = useDisconnect();
  const doConnect = React11.useCallback(
    (connector) => {
      const target = connector ?? connectors[0];
      if (!target) return;
      clearDisconnected(WEB3_EVM_CONNECTION_ID);
      connect({ connector: target });
    },
    [connect, connectors]
  );
  const doDisconnect = React11.useCallback(() => {
    markDisconnected(WEB3_EVM_CONNECTION_ID);
    disconnect();
  }, [disconnect]);
  return {
    isConnected,
    address,
    connectors,
    connect: doConnect,
    disconnect: doDisconnect,
    isConnecting: isPending,
    error: error ?? null
  };
}

// src/wallet/injected.ts
function injected2() {
  return (typeof window !== "undefined" ? window : {}) ?? {};
}
var getEvmProvider = () => injected2().ethereum;
var getSolanaProvider = () => injected2().solana;
var getBitcoinProvider = () => injected2().unisat;

// src/wallet/adapters.ts
function createInjectedEvmAdapter(options = {}) {
  const getProvider = options.getProvider ?? getEvmProvider;
  const toAccounts = (addrs, chainId) => {
    const chain = chainId ? getChainMetadata(chainId)?.slug ?? chainId : void 0;
    return addrs.map((a) => ({ address: a.toLowerCase(), family: ChainFamily.EVM, chain }));
  };
  return {
    id: options.id ?? "injected-evm",
    name: options.name ?? "Browser wallet",
    family: ChainFamily.EVM,
    isAvailable: () => Boolean(getProvider()),
    async connect() {
      const provider = getProvider();
      if (!provider) throw new Error("No EVM wallet found");
      const addrs = await provider.request({ method: "eth_requestAccounts" });
      const chainId = await provider.request({ method: "eth_chainId" }).catch(() => void 0);
      return toAccounts(addrs ?? [], chainId);
    },
    async getAccounts() {
      const provider = getProvider();
      if (!provider) return [];
      const addrs = await provider.request({ method: "eth_accounts" }).catch(() => []);
      return toAccounts(addrs ?? []);
    },
    async disconnect() {
    },
    subscribe(listener) {
      const provider = getProvider();
      if (!provider?.on || !provider.removeListener) return () => void 0;
      const handler = (addrs) => listener(toAccounts(addrs ?? []));
      provider.on("accountsChanged", handler);
      return () => provider.removeListener?.("accountsChanged", handler);
    }
  };
}
function createSolanaAdapter(options = {}) {
  const getProvider = options.getProvider ?? getSolanaProvider;
  const account = (address) => ({ address, family: ChainFamily.SOLANA, chain: "solana" });
  return {
    id: options.id ?? "phantom",
    name: options.name ?? "Phantom",
    family: ChainFamily.SOLANA,
    isAvailable: () => Boolean(getProvider()),
    async connect() {
      const provider = getProvider();
      if (!provider) throw new Error("No Solana wallet found");
      const res = await provider.connect();
      return [account(res.publicKey.toString())];
    },
    async getAccounts() {
      const pk = getProvider()?.publicKey;
      return pk ? [account(pk.toString())] : [];
    },
    async disconnect() {
      await getProvider()?.disconnect();
    }
  };
}
function createBitcoinAdapter(options = {}) {
  const getProvider = options.getProvider ?? getBitcoinProvider;
  const toAccounts = (addrs) => (addrs ?? []).map((a) => ({ address: a, family: ChainFamily.UTXO, chain: "bitcoin" }));
  return {
    id: options.id ?? "unisat",
    name: options.name ?? "Unisat",
    family: ChainFamily.UTXO,
    isAvailable: () => Boolean(getProvider()),
    async connect() {
      const provider = getProvider();
      if (!provider) throw new Error("No Bitcoin wallet found");
      return toAccounts(await provider.requestAccounts());
    },
    async getAccounts() {
      const provider = getProvider();
      if (!provider) return [];
      return toAccounts(await provider.getAccounts().catch(() => []));
    },
    async disconnect() {
    }
  };
}
function defaultWalletAdapters() {
  return [createInjectedEvmAdapter(), createSolanaAdapter(), createBitcoinAdapter()];
}
var WalletsContext = React11.createContext(null);
function WalletProvider({ adapters, autoConnect = true, children }) {
  const adapterList = React11.useMemo(() => adapters ?? defaultWalletAdapters(), [adapters]);
  const value = useWalletHubState(adapterList, autoConnect);
  return /* @__PURE__ */ jsx(WalletsContext.Provider, { value, children });
}
function useWallets() {
  const ctx = React11.useContext(WalletsContext);
  if (!ctx) throw new Error("useWallets must be used within a <WalletProvider>");
  return ctx;
}
function useWalletHubState(adapters, autoConnect) {
  const [accountsByFamily, setAccountsByFamily] = React11.useState({});
  const [status, setStatus] = React11.useState({});
  const [errors, setErrors] = React11.useState({});
  const byId = React11.useMemo(() => new Map(adapters.map((a) => [a.id, a])), [adapters]);
  const resolve = React11.useCallback(
    (target) => typeof target === "string" ? byId.get(target) : target,
    [byId]
  );
  const applyAccounts = React11.useCallback((adapter, accounts2) => {
    setAccountsByFamily((prev) => ({ ...prev, [adapter.family]: accounts2 }));
    setStatus((prev) => ({ ...prev, [adapter.id]: accounts2.length > 0 ? "connected" : "disconnected" }));
  }, []);
  React11.useEffect(() => {
    if (!autoConnect) return void 0;
    let alive = true;
    const unsubs = [];
    for (const adapter of adapters) {
      if (!adapter.isAvailable()) continue;
      if (isDisconnected(adapter.id)) continue;
      adapter.getAccounts().then((accounts2) => alive && accounts2.length > 0 && applyAccounts(adapter, accounts2)).catch(() => void 0);
      if (adapter.subscribe) {
        unsubs.push(adapter.subscribe((accounts2) => alive && applyAccounts(adapter, accounts2)));
      }
    }
    return () => {
      alive = false;
      unsubs.forEach((u) => u());
    };
  }, [adapters, autoConnect, applyAccounts]);
  const connect = React11.useCallback(
    async (target) => {
      const adapter = resolve(target);
      if (!adapter) return;
      setStatus((prev) => ({ ...prev, [adapter.id]: "connecting" }));
      setErrors((prev) => ({ ...prev, [adapter.id]: void 0 }));
      clearDisconnected(adapter.id);
      try {
        applyAccounts(adapter, await adapter.connect());
      } catch (e) {
        setStatus((prev) => ({ ...prev, [adapter.id]: "error" }));
        setErrors((prev) => ({ ...prev, [adapter.id]: e instanceof Error ? e.message : "Connection failed" }));
      }
    },
    [resolve, applyAccounts]
  );
  const disconnect = React11.useCallback(
    async (target) => {
      const adapter = resolve(target);
      if (!adapter) return;
      markDisconnected(adapter.id);
      try {
        await adapter.disconnect();
      } finally {
        setAccountsByFamily((prev) => ({ ...prev, [adapter.family]: [] }));
        setStatus((prev) => ({ ...prev, [adapter.id]: "disconnected" }));
      }
    },
    [resolve]
  );
  const accounts = React11.useMemo(() => Object.values(accountsByFamily).flat().filter(Boolean), [
    accountsByFamily
  ]);
  return React11.useMemo(
    () => ({
      adapters,
      available: adapters.filter((a) => a.isAvailable()),
      accounts,
      accountsByFamily,
      status: (id) => status[id] ?? "disconnected",
      error: (id) => errors[id],
      connect,
      disconnect,
      isConnected: (family) => family ? (accountsByFamily[family]?.length ?? 0) > 0 : accounts.length > 0
    }),
    [adapters, accounts, accountsByFamily, status, errors, connect, disconnect]
  );
}
function ChainPicker({
  slugs,
  includeTestnets = false,
  placeholder = "Select chain",
  className
}) {
  const { chain, switchToSlug } = useSelectedChain();
  const options = React11.useMemo(() => {
    const wanted = slugs ? new Set(slugs) : null;
    return registry.all().filter(
      (c) => c.family === ChainFamily.EVM && (includeTestnets || !c.isTestnet) && (!wanted || wanted.has(c.slug))
    );
  }, [slugs, includeTestnets]);
  return /* @__PURE__ */ jsxs(Select, { value: chain?.slug ?? "", onValueChange: (slug) => switchToSlug(slug), children: [
    /* @__PURE__ */ jsx(SelectTrigger, { className: cn("w-[200px]", className), children: /* @__PURE__ */ jsx(SelectValue, { placeholder }) }),
    /* @__PURE__ */ jsx(SelectContent, { children: options.map((c) => /* @__PURE__ */ jsx(SelectItem, { value: c.slug, children: /* @__PURE__ */ jsx(ChainAvatar, { chain: c.slug, size: "sm" }) }, c.slug)) })
  ] });
}
var FAMILY_LABEL = {
  evm: "EVM",
  utxo: "Bitcoin",
  solana: "Solana",
  cosmos: "Cosmos",
  other: "Other"
};
function WalletMenu({ onlyAvailable = false, className }) {
  const { adapters, accountsByFamily, status, error, connect, disconnect } = useWallets();
  const rows = onlyAvailable ? adapters.filter((a) => a.isAvailable()) : adapters;
  return /* @__PURE__ */ jsx("div", { className: cn("divide-y rounded-md border", className), children: rows.map((adapter) => {
    const available = adapter.isAvailable();
    const account = accountsByFamily[adapter.family]?.[0];
    const state = status(adapter.id);
    const err = error(adapter.id);
    const connected = state === "connected" && account;
    return /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3 px-3 py-2.5", children: [
      /* @__PURE__ */ jsxs("div", { className: "min-w-0 flex-1", children: [
        /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
          /* @__PURE__ */ jsx("span", { className: "text-sm font-medium", children: adapter.name }),
          /* @__PURE__ */ jsx(Badge, { variant: "outline", className: "text-[10px]", children: FAMILY_LABEL[adapter.family] })
        ] }),
        connected ? /* @__PURE__ */ jsx("p", { className: "font-mono text-xs text-muted-foreground", children: truncateAddress(account.address) }) : err ? /* @__PURE__ */ jsx("p", { className: "text-xs text-destructive", children: err }) : !available ? /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: "Not installed" }) : null
      ] }),
      connected ? /* @__PURE__ */ jsx(Button, { type: "button", size: "sm", variant: "outline", onClick: () => void disconnect(adapter), children: "Disconnect" }) : /* @__PURE__ */ jsx(
        Button,
        {
          type: "button",
          size: "sm",
          disabled: !available || state === "connecting",
          onClick: () => void connect(adapter),
          children: state === "connecting" ? "Connecting\u2026" : "Connect"
        }
      )
    ] }, adapter.id);
  }) });
}

export { ChainPicker, WEB3_DEFAULT_CHAIN_SLUG, WEB3_DEFAULT_RPC_BASE, WEB3_EVM_CONNECTION_ID, WEB3_PACKAGE, WEB3_SELECTED_CHAIN_KEY, WEB3_SIGNER_ROOT_ID, WEB3_SIGNER_TTL_MS, WalletMenu, WalletProvider, Web3Provider, autheEvmViemChains, clearDisconnected, createAutheWagmiConfig, createBitcoinAdapter, createInjectedEvmAdapter, createRpcContractReader, createSolanaAdapter, defaultWalletAdapters, isDisconnected, markDisconnected, recoverSigner, resolveRpcUrls, stableArgsKey, toViemChain, useAddressInventory, useAutheSigner, useContractRead, useContractReads, useContractWrite, useEvmSignMessage, useEvmStorageAt, useSelectedChain, useWalletConnection, useWallets, verifyEvmSignature };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map